import { useEffect, useState } from 'react';
import { MESSAGE_STATUS } from 'src/constants/messageStatus';
import { Message } from 'src/services/messages_services/useGetMessageApi';
import { MessageDetailsInboxResponse } from 'src/services/messages_services/useMessageDetailsInboxApi';
import { toApiError } from 'src/utils/errorHandling';

interface BatchResponseDto {
  success: boolean;
  message?: string;
  errors?: string[];
}

interface UseInboxMessageDetailsProps {
  visible: boolean;
  message: Message | null;
  messageIdString: string;
  shouldUseApi: boolean;
  authClientId: string;
  getMessageDetails: (params?: undefined) => Promise<MessageDetailsInboxResponse | null>;
  setMessageToRead: (params?: { clientId: string; messageId: string }) => Promise<BatchResponseDto>;
  setCurrentMessageStatus: (status: string) => void;
  refreshTable: () => void;
  isMessageLoading: boolean;
}

/**
 * Custom hook to manage inbox message details fetching and marking as read
 * Separates concerns into two focused effects
 */
export const useInboxMessageDetails = ({
  visible,
  message,
  messageIdString,
  shouldUseApi,
  authClientId,
  getMessageDetails,
  setMessageToRead,
  setCurrentMessageStatus,
  refreshTable,
}: UseInboxMessageDetailsProps) => {
  const [messageDetails, setMessageDetails] = useState<MessageDetailsInboxResponse | null>(null);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  // Effect 1: Mark message as read if it's new
  useEffect(() => {
    if (!visible || !message?.messageId || !messageIdString) return;
    if (message?.decodedStatus.toLowerCase() !== MESSAGE_STATUS.NEW) return;

    const markAsRead = async () => {
      try {
        const clientId = authClientId || '';
        if (!clientId) {
          console.warn('Cannot mark message as read: missing clientId');
          return;
        }

        await setMessageToRead({ clientId, messageId: messageIdString });
        setCurrentMessageStatus(MESSAGE_STATUS.READ);
        
        try {
          refreshTable();
        } catch (refreshError) {
          console.warn('Failed to refresh grid after marking message as read:', refreshError);
        }
      } catch (error: unknown) {
        console.warn('Failed to mark message as read:', toApiError(error).message);
      }
    };

    markAsRead();
  }, [
    visible,
    message?.messageId,
    message?.decodedStatus,
    messageIdString,
    authClientId,
    setMessageToRead,
    setCurrentMessageStatus,
    refreshTable,
  ]);

  // Effect 2: Fetch message details
  useEffect(() => {
    let isMounted = true;

    const fetchDetails = async () => {
      if (!visible || !message?.messageId || !shouldUseApi) {
        if (isMounted) {
          setMessageDetails(null);
          setDetailsError(null);
        }
        return;
      }

      try {
        if (isMounted) {
          setMessageDetails(null);
          setDetailsError(null);
        }

        if (!isMounted || !visible || !message?.messageId || !shouldUseApi) {
          return;
        }

        const details = await getMessageDetails();

        if (isMounted && details) {
          setMessageDetails(details);
        }
      } catch (error: unknown) {
        const apiError = toApiError(error);

        if (isMounted && visible) {
          if (apiError.name === 'AbortError' || apiError.message.includes('Request cancelled')) {
            return;
          }

          setDetailsError(
            `Additional contact details unavailable (${
              apiError.response?.status || 'Network error'
            })`
          );
          setMessageDetails(null);
        }
      }
    };

    if (visible && message?.messageId && shouldUseApi) {
      fetchDetails().catch((error: unknown) => {
        const apiError = toApiError(error);

        if (isMounted && visible) {
          if (apiError.name === 'AbortError' || apiError.message.includes('Request cancelled')) {
            return;
          }

          console.error('Unexpected error in fetchDetails for messageId:', message?.messageId);
          console.error('Error object:', apiError);
          setDetailsError('Additional contact details unavailable');
          setMessageDetails(null);
        }
      });
    } else {
      if (isMounted) {
        setMessageDetails(null);
        setDetailsError(null);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [visible, message?.messageId, shouldUseApi, getMessageDetails]);

  return {
    messageDetails,
    detailsError,
  };
};
